import { IWatrRequest } from '@nkr/models/index';
import { AdvancedWorkflowStatus, OrganType, ReimbursementType, WageAndTravelCategory, WageAndTravelReimbursementRequestStatus, WageAndTravelStatus } from '@nkr/enums/index';
import { computeIsInternationallyEligible } from './utils/international-eligibility';

export const ADVANCED_INVITE_FAILED_STATUS_CODE = 10;
export const DONOR_REMOTE_STATUS_WORKUP_COMPLETE = 15;
export const DONOR_REMOTE_STATUS_RULED_OUT = 200;
export const ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY = 'Waiting for Completed workup';
/** Intl kidney program after workup: standard DER verify/pay — not domestic card advanced reimbursement. */
export const INTL_POST_WORKUP_STATUS_DISPLAY = 'Post Workup';
export const INTL_POST_WORKUP_STATUS_TOOLTIP =
    'International donor, post-workup: standard expense reimbursement (not card advanced reimbursement).';
export const PEND_ADV_REIMBURSE_STATUS_DISPLAY = 'Pend Adv Reimburse';
export const PEND_ADV_REIMBURSE_STATUS_TOOLTIP = 'Pending Advanced Reimbursement';

export type AdvancedRollupBucket =
    | 'TODO_NKR'
    | 'DECLINED'
    | 'ON_HOLD'
    | 'PENDING_DONOR'
    | 'PENDING_BANK_SUBMISSIONS'
    | 'COMPLETE';

export type AdvancedRollupFilterValue = AdvancedRollupBucket | 'ALL' | 'SEARCH';

export const ADVANCED_REIMBURSEMENT_TAB_ORDER: ReadonlyArray<AdvancedRollupFilterValue> = [
    'TODO_NKR',
    'DECLINED',
    'ON_HOLD',
    'PENDING_DONOR',
    'PENDING_BANK_SUBMISSIONS',
    'COMPLETE',
    'ALL',
    'SEARCH',
];

export function getAdvancedRollupFilterPillOrder(): ReadonlyArray<AdvancedRollupFilterValue> {
    return ADVANCED_REIMBURSEMENT_TAB_ORDER;
}

export const ADVANCED_INCOMPLETE_DATA_DISPLAY = 'Advanced reimbursement data is incomplete; contact support.';
export const ADVANCED_INVITE_PENDING_DISPLAY = 'Invite Sent';
export const ADVANCED_DONOR_SUBMISSION_DISPLAY = 'Approved: Awaiting Donor Submission';
export const ADVANCED_DONOR_SUBMISSION_SHORT_DISPLAY = 'Approved';

function isInviteSentPendingRow(progress: IAdvancedProgressRead | undefined, canonical: string): boolean {
    return progress?.invitePending === true || canonical === ADVANCED_INVITE_PENDING_DISPLAY;
}

const ADVANCED_WORKFLOW_DISPLAY_BY_CODE: Record<number, string> = {
    [AdvancedWorkflowStatus.Unknown]: 'Unknown',
    [AdvancedWorkflowStatus.InviteFailed]: 'Invitation could not be sent',
    [AdvancedWorkflowStatus.AwaitingDonorProfile]: 'Profile Incomplete',
    [AdvancedWorkflowStatus.AwaitingApplicationConfirmation]: 'Awaiting Application Confirmation',
    [AdvancedWorkflowStatus.AwaitingNkrReview]: 'Awaiting NKR Review',
    [AdvancedWorkflowStatus.AwaitingDonorSubmission]: ADVANCED_DONOR_SUBMISSION_DISPLAY,
    [AdvancedWorkflowStatus.PendingNkrVerification]: 'Awaiting NKR Verification',
    [AdvancedWorkflowStatus.ReadyToReconcile]: 'Ready to Reconcile',
    [AdvancedWorkflowStatus.OnHold]: 'On Hold',
    [AdvancedWorkflowStatus.Complete]: 'Closed',
    [AdvancedWorkflowStatus.Cancelled]: 'Cancelled',
    [AdvancedWorkflowStatus.Declined]: 'Declined',
};

export interface IAdvancedProgressRead {
    status?: string;
    statusCode?: number;
    lastMilestoneDate?: string | undefined;
    workupScheduledDate?: string | undefined;
    workupCompletedDate?: string | undefined;
    preOpScheduledDate?: string | undefined;
    surgeryScheduledDate?: string | undefined;
    networkMilestoneLabel?: string | undefined;
    networkRemoteStatus?: number | undefined;
    processorCardReloadLabel?: string | undefined;
    cardReloadPhase?: string | undefined;
    invited?: boolean | undefined;
    profileComplete?: boolean | undefined;
    applicationConfirmed?: boolean | undefined;
    requestSubmitted?: boolean | undefined;
    nkrReviewed?: boolean | undefined;
    invitePending?: boolean | undefined;
    onTrack?: boolean | undefined;
}

export function readAdvancedProgressForBuckets(data: IWatrRequest): IAdvancedProgressRead | undefined {
    const raw = (data as unknown as { advancedProgress?: Record<string, unknown>; AdvancedProgress?: Record<string, unknown> }).advancedProgress
        ?? (data as unknown as { AdvancedProgress?: Record<string, unknown> }).AdvancedProgress;
    if (!raw) {
        return undefined;
    }
    return {
        status: (raw.status ?? raw.Status) as string | undefined,
        statusCode: (raw.statusCode ?? raw.StatusCode) as number | undefined,
        lastMilestoneDate: (raw.lastMilestoneDate ?? raw.LastMilestoneDate) as string | undefined,
        workupScheduledDate: (raw.workupScheduledDate ?? raw.WorkupScheduledDate) as string | undefined,
        workupCompletedDate: (raw.workupCompletedDate ?? raw.WorkupCompletedDate) as string | undefined,
        preOpScheduledDate: (raw.preOpScheduledDate ?? raw.PreOpScheduledDate) as string | undefined,
        surgeryScheduledDate: (raw.surgeryScheduledDate ?? raw.SurgeryScheduledDate) as string | undefined,
        networkMilestoneLabel: (raw.networkMilestoneLabel ?? raw.NetworkMilestoneLabel) as string | undefined,
        networkRemoteStatus: (raw.networkRemoteStatus ?? raw.NetworkRemoteStatus) as number | undefined,
        processorCardReloadLabel: (raw.processorCardReloadLabel ?? raw.ProcessorCardReloadLabel) as string | undefined,
        cardReloadPhase: (raw.cardReloadPhase ?? raw.CardReloadPhase) as string | undefined,
        invited: (raw.invited ?? raw.Invited) as boolean | undefined,
        profileComplete: (raw.profileComplete ?? raw.ProfileComplete) as boolean | undefined,
        applicationConfirmed: (raw.applicationConfirmed ?? raw.ApplicationConfirmed) as boolean | undefined,
        requestSubmitted: (raw.requestSubmitted ?? raw.RequestSubmitted) as boolean | undefined,
        nkrReviewed: (raw.nkrReviewed ?? raw.NkrReviewed) as boolean | undefined,
        invitePending: (raw.invitePending ?? raw.InvitePending) as boolean | undefined,
        onTrack: (raw.onTrack ?? raw.OnTrack) as boolean | undefined,
    };
}

export function readIsInternationallyEligibleForWatr(data: IWatrRequest): boolean {
    const row = data as unknown as {
        isInternationallyEligible?: boolean | null;
        IsInternationallyEligible?: boolean | null;
        country?: number | null;
        Country?: number | null;
        filesTaxesInUs?: boolean | null;
        FilesTaxesInUs?: boolean | null;
    };
    const flagged = row.isInternationallyEligible ?? row.IsInternationallyEligible;
    if (flagged === true) {
        return true;
    }
    if (flagged === false) {
        return false;
    }
    const country = row.country ?? row.Country;
    const filesTaxesInUs = row.filesTaxesInUs ?? row.FilesTaxesInUs;
    return computeIsInternationallyEligible(country, filesTaxesInUs);
}

export function isIntlWorkupReimbursementProgram(data: IWatrRequest | undefined): boolean {
    if (!data || !readIsInternationallyEligibleForWatr(data)) {
        return false;
    }
    const organ = data.donationOrganType;
    return organ === OrganType.Kidney || organ === OrganType.Liver;
}

export function hasPendingTravelApprovalForWatr(data: IWatrRequest): boolean {
    return (data.requestTravels || []).some(
        x => x.status === WageAndTravelReimbursementRequestStatus.PendingApproval
    );
}

export function isIntlStandardDerVerify(data: IWatrRequest): boolean {
    return readIsInternationallyEligibleForWatr(data)
        && !data.donationConfirmed
        && hasPendingTravelApprovalForWatr(data);
}

export function hasAdminApprovedTravelForWatr(data: IWatrRequest): boolean {
    return (data.requestTravels || []).some(
        x => x.status === WageAndTravelReimbursementRequestStatus.AdminApproved
    );
}

export function isIntlStandardDerReimburseTravel(data: IWatrRequest, category: WageAndTravelCategory): boolean {
    if (!readIsInternationallyEligibleForWatr(data)) {
        return false;
    }
    if (data.donationConfirmed) {
        return false;
    }
    if (!hasAdminApprovedTravelForWatr(data)) {
        return false;
    }
    return category === WageAndTravelCategory.PendingWire
        || category === WageAndTravelCategory.PendingPayment;
}

/** Intl post-workup wire/ACH reimbursement entry — Workup/Pre-Op types and visit caps (not classic DER type list). */
export function isIntlArVisitReimbursementEntry(data: IWatrRequest | undefined): boolean {
    if (!data) {
        return false;
    }
    if (!readIsInternationallyEligibleForWatr(data)) {
        return false;
    }
    if (data.donationConfirmed) {
        return false;
    }
    if (!readAdvancedProgressForBuckets(data)) {
        return false;
    }
    return hasAdminApprovedTravelForWatr(data);
}

/** Intl workup program uses standard WATR verify/pay — not card AR actions on the Advanced Reimbursement grid. */
export function usesIntlStandardDerAdminActions(data: IWatrRequest | undefined): boolean {
    if (!data) {
        return false;
    }
    if (!isIntlWorkupReimbursementProgram(data)) {
        return false;
    }
    if (data.donationConfirmed) {
        return false;
    }
    return !!readAdvancedProgressForBuckets(data);
}

function readIsInternationallyEligible(data: IWatrRequest): boolean {
    return readIsInternationallyEligibleForWatr(data);
}

function hasReachedWorkupComplete(progress: IAdvancedProgressRead | undefined): boolean {
    if (!progress) {
        return false;
    }
    if (progress.workupCompletedDate?.trim()) {
        return true;
    }
    // Liver/DSD rows may expose remote workup-complete without a completed date on the list payload.
    return progress.networkRemoteStatus === DONOR_REMOTE_STATUS_WORKUP_COMPLETE;
}

function isAwaitingNkrReviewProgress(progress: IAdvancedProgressRead | undefined): boolean {
    if (!progress) {
        return false;
    }
    return progress.statusCode === AdvancedWorkflowStatus.AwaitingNkrReview
        || progress.statusCode === AdvancedWorkflowStatus.AwaitingDonorProfile
        || progress.status === 'Awaiting NKR Review'
        || progress.status === ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY
        || progress.status === 'Awaiting Workup Complete';
}

export function isIntlBlockedFromNkrTodo(data: IWatrRequest, progress?: IAdvancedProgressRead): boolean {
    const p = progress ?? readAdvancedProgressForBuckets(data);
    if (p?.status === ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY
        || p?.status === 'Awaiting Workup Complete') {
        return true;
    }
    if (!isAwaitingNkrReviewProgress(p)) {
        return false;
    }
    if (!readIsInternationallyEligible(data)) {
        return false;
    }
    return !hasReachedWorkupComplete(p);
}

export const INVITATION_COULD_NOT_BE_SENT_DISPLAY = 'Invitation could not be sent';
export const STATUS_UNAVAILABLE_DISPLAY = 'Status unavailable';

export function showIntlPostWorkupProgramStatus(data: IWatrRequest): boolean {
    if (!readIsInternationallyEligibleForWatr(data)) {
        return false;
    }
    if (data.donationConfirmed || data.status === WageAndTravelStatus.Donated) {
        return false;
    }
    if (intlLeftAdvancedOperationalQueue(data)) {
        return true;
    }
    const progress = readAdvancedProgressForBuckets(data);
    if (isIntlBlockedFromNkrTodo(data, progress)) {
        return false;
    }
    return hasReachedWorkupComplete(progress);
}

export function resolveDerProgramCap(data: IWatrRequest): number {
    const cap = data?.derProgramCap;

    if (typeof cap === 'number' && cap > 0) {
        return cap;
    }

    return 0;
}

const WORKUP_PURSE_TRIP_REASON_IDS = new Set([1, 4, 8]);

function isWorkupPurseReimbursementType(type: number): boolean {
    return type === ReimbursementType.InternationalWorkup
        || type === ReimbursementType.AdvancedReimbursement
        || type === ReimbursementType.AdvancedReimbursementPreOp;
}

export function isPostDonationDerPurse(data: IWatrRequest | undefined): boolean {
    if (!data) {
        return false;
    }
    return data.donationConfirmed === true || data.status === WageAndTravelStatus.Donated;
}

export function shouldExcludeTravelFromStandardDerPrior(
    data: IWatrRequest,
    travel: { id?: number; reimbursementId?: number; trips?: Array<{ tripReasonId?: number }>; roundTripInstances?: Array<{ tripReason?: string }> }
): boolean {
    if (!isPostDonationDerPurse(data) || !isIntlWorkupReimbursementProgram(data)) {
        return false;
    }

    const tripReasons = (travel.trips || [])
        .map(t => t.tripReasonId)
        .filter((id): id is number => typeof id === 'number');
    if (tripReasons.some(id => WORKUP_PURSE_TRIP_REASON_IDS.has(id))) {
        return true;
    }

    const reasonLabels = (travel.roundTripInstances || [])
        .map(x => (x.tripReason || '').trim().toLowerCase());
    if (reasonLabels.some(label =>
        label === 'work-up'
        || label === 'workup'
        || label === 'pre-op'
        || label === 'preop'
        || label === 'international workup')) {
        return true;
    }

    const reimbursementId = travel.reimbursementId;
    if (reimbursementId == null || reimbursementId <= 0) {
        return (data.reimbursements || []).some(r => r != null && isWorkupPurseReimbursementType(r.reimbursementType));
    }

    const linked = (data.reimbursements || []).find(r => r != null && r.id === reimbursementId);
    return linked != null && isWorkupPurseReimbursementType(linked.reimbursementType);
}

export function getWatrGridStatusDisplay(data: IWatrRequest, isAdvancedReimbursementArea: boolean): string {
    if (showIntlPostWorkupProgramStatus(data)) {
        return INTL_POST_WORKUP_STATUS_DISPLAY;
    }
    if (isAdvancedReimbursementArea) {
        return getCanonicalAdvancedReimbursementStatus(data);
    }
    if (data.status === WageAndTravelStatus.PendingAdvancedReimbursement) {
        return PEND_ADV_REIMBURSE_STATUS_DISPLAY;
    }
    return data.statusDescription ?? '';
}

export function getWatrGridStatusTooltip(data: IWatrRequest, displayStatus: string): string | undefined {
    if (displayStatus === INTL_POST_WORKUP_STATUS_DISPLAY) {
        return INTL_POST_WORKUP_STATUS_TOOLTIP;
    }
    if (data.status === WageAndTravelStatus.PendingAdvancedReimbursement) {
        return PEND_ADV_REIMBURSE_STATUS_TOOLTIP;
    }
    if (displayStatus !== data.statusDescription) {
        return data.statusDescription;
    }
    return undefined;
}

const WATR_GRID_STATUS_SHORT_DISPLAY: Record<string, string> = {
    [ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.AwaitingApplicationConfirmation]]: 'Awaiting confirmation',
    [ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.AwaitingNkrReview]]: 'NKR review',
    [ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.PendingNkrVerification]]: 'NKR verify',
    [ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.ReadyToReconcile]]: 'Reconcile',
    [ADVANCED_DONOR_SUBMISSION_DISPLAY]: ADVANCED_DONOR_SUBMISSION_SHORT_DISPLAY,
    'Awaiting Donor Submission': ADVANCED_DONOR_SUBMISSION_SHORT_DISPLAY,
    [ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY]: 'Workup pending',
    [ADVANCED_INCOMPLETE_DATA_DISPLAY]: 'Data incomplete',
    [STATUS_UNAVAILABLE_DISPLAY]: 'Unavailable',
    [INVITATION_COULD_NOT_BE_SENT_DISPLAY]: 'Invite failed'
};

export function getWatrGridStatusShortDisplay(displayStatus: string): string {
    if (!displayStatus) {
        return '';
    }

    return WATR_GRID_STATUS_SHORT_DISPLAY[displayStatus] || displayStatus;
}

export function getCanonicalAdvancedReimbursementStatus(data: IWatrRequest): string {
    const progress = readAdvancedProgressForBuckets(data);
    if (!progress) {
        return STATUS_UNAVAILABLE_DISPLAY;
    }

    if (progress.invitePending === true) {
        return ADVANCED_INVITE_PENDING_DISPLAY;
    }

    if (isIntlBlockedFromNkrTodo(data, progress)) {
        return ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY;
    }

    if (showIntlPostWorkupProgramStatus(data)) {
        return INTL_POST_WORKUP_STATUS_DISPLAY;
    }

    if (progress.onTrack === false && progress.invited === true && !progress.status?.trim()) {
        return ADVANCED_INVITE_PENDING_DISPLAY;
    }

    if (progress.statusCode === ADVANCED_INVITE_FAILED_STATUS_CODE || progress.status === 'Invite Failed to Send') {
        return INVITATION_COULD_NOT_BE_SENT_DISPLAY;
    }

    const trimmed = progress.status?.trim();
    if (trimmed) {
        if (trimmed === 'Complete' || trimmed === 'Funded' || trimmed === 'Inactive Card') {
            return 'Inactive Cards';
        }
        return trimmed;
    }

    if (progress.statusCode != null && ADVANCED_WORKFLOW_DISPLAY_BY_CODE[progress.statusCode] !== undefined) {
        return ADVANCED_WORKFLOW_DISPLAY_BY_CODE[progress.statusCode];
    }

    const hasExplicitBooleans =
        progress.invited !== undefined
        || progress.profileComplete !== undefined
        || progress.requestSubmitted !== undefined;

    if (hasExplicitBooleans) {
        const invited = progress.invited === true;
        const profileComplete = progress.profileComplete === true;
        const applicationConfirmed = progress.applicationConfirmed === true;
        const requestSubmitted = progress.requestSubmitted === true;

        if (!invited) {
            return INVITATION_COULD_NOT_BE_SENT_DISPLAY;
        }
        if (requestSubmitted) {
            return ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.PendingNkrVerification];
        }
        if (!applicationConfirmed && profileComplete) {
            return ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.AwaitingApplicationConfirmation];
        }
        if (profileComplete && applicationConfirmed) {
            return ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.AwaitingNkrReview];
        }
        return ADVANCED_WORKFLOW_DISPLAY_BY_CODE[AdvancedWorkflowStatus.AwaitingDonorProfile];
    }

    return STATUS_UNAVAILABLE_DISPLAY;
}

export function isAdvancedInviteDeliveryIssue(data: IWatrRequest): boolean {
    return getCanonicalAdvancedReimbursementStatus(data) === INVITATION_COULD_NOT_BE_SENT_DISPLAY;
}

export function isAdvancedStatusUnavailable(data: IWatrRequest): boolean {
    return getCanonicalAdvancedReimbursementStatus(data) === STATUS_UNAVAILABLE_DISPLAY;
}

function rollupBucketFromStatusCode(code: number | undefined): AdvancedRollupBucket | null {
    if (code === undefined || code === null) {
        return null;
    }
    if (code === AdvancedWorkflowStatus.AwaitingNkrReview
        || code === AdvancedWorkflowStatus.PendingNkrVerification
        || code === AdvancedWorkflowStatus.ReadyToReconcile) {
        return 'TODO_NKR';
    }
    if (code === AdvancedWorkflowStatus.AwaitingDonorSubmission) {
        return 'PENDING_BANK_SUBMISSIONS';
    }
    if (code === AdvancedWorkflowStatus.AwaitingDonorProfile
        || code === AdvancedWorkflowStatus.AwaitingApplicationConfirmation) {
        return 'PENDING_DONOR';
    }
    if (code === AdvancedWorkflowStatus.Complete) {
        return 'COMPLETE';
    }
    if (code === AdvancedWorkflowStatus.OnHold) {
        return 'ON_HOLD';
    }
    if (code === AdvancedWorkflowStatus.Declined || code === AdvancedWorkflowStatus.Cancelled) {
        return 'DECLINED';
    }
    return null;
}

function intlLeftAdvancedOperationalQueue(data: IWatrRequest): boolean {
    if (!readIsInternationallyEligibleForWatr(data)) {
        return false;
    }
    if (data.pendingDERVerify === true || data.pendingDERPayment === true) {
        return true;
    }
    const travels = data.requestTravels || [];
    return travels.some(x => {
        const status = (x as { status?: number; Status?: number }).status
            ?? (x as { Status?: number }).Status;
        return status === WageAndTravelReimbursementRequestStatus.PendingApproval
            || status === WageAndTravelReimbursementRequestStatus.AutoApproved
            || status === WageAndTravelReimbursementRequestStatus.CenterApproved
            || status === WageAndTravelReimbursementRequestStatus.AdminApproved;
    });
}

export function resolveAdvancedRollupBucket(data: IWatrRequest): AdvancedRollupBucket | null {
    const progress = readAdvancedProgressForBuckets(data);
    const canonical = getCanonicalAdvancedReimbursementStatus(data);

    if (showIntlPostWorkupProgramStatus(data) && !intlLeftAdvancedOperationalQueue(data)) {
        return 'PENDING_DONOR';
    }

    if (intlLeftAdvancedOperationalQueue(data)) {
        return 'PENDING_DONOR';
    }

    if (isInviteSentPendingRow(progress, canonical)) {
        return 'PENDING_DONOR';
    }

    if (canonical === STATUS_UNAVAILABLE_DISPLAY || canonical === INVITATION_COULD_NOT_BE_SENT_DISPLAY) {
        return null;
    }

    if (isIntlBlockedFromNkrTodo(data, progress)
        || canonical === ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY
        || progress?.status === ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY) {
        return 'PENDING_DONOR';
    }

    if (progress?.statusCode === ADVANCED_INVITE_FAILED_STATUS_CODE) {
        return null;
    }

    const fromCode = rollupBucketFromStatusCode(progress?.statusCode);
    if (fromCode != null) {
        return fromCode;
    }

    if (canonical === ADVANCED_INCOMPLETE_DATA_DISPLAY) {
        return 'TODO_NKR';
    }

    switch (canonical) {
        case INTL_POST_WORKUP_STATUS_DISPLAY:
            return 'PENDING_DONOR';
        case 'Awaiting NKR Review':
        case 'Awaiting NKR Verification':
        case 'Ready to Reconcile':
            return 'TODO_NKR';
        case ADVANCED_DONOR_SUBMISSION_DISPLAY:
        case 'Awaiting Donor Submission':
            return 'PENDING_BANK_SUBMISSIONS';
        case 'Profile Incomplete':
        case 'Awaiting Donor Profile':
        case 'Awaiting Application Confirmation':
        case ADVANCED_AWAITING_WORKUP_COMPLETE_DISPLAY:
        case ADVANCED_INVITE_PENDING_DISPLAY:
            return 'PENDING_DONOR';
        case 'Complete':
        case 'Funded':
        case 'Inactive Card':
        case 'Inactive Cards':
        case 'Closed':
            return 'COMPLETE';
        case 'On Hold':
            return 'ON_HOLD';
        case 'Cancelled':
        case 'Declined':
            return 'DECLINED';
        case 'Unknown':
            return null;
        default:
            return null;
    }
}

export function advancedRollupTabLabel(tab: AdvancedRollupFilterValue, isNkrSuper: boolean): string {
    switch (tab) {
        case 'TODO_NKR':
            return isNkrSuper ? 'To-Do' : 'NKR review';
        case 'DECLINED':
            return 'Declined';
        case 'ON_HOLD':
            return 'On Hold';
        case 'PENDING_DONOR':
            return 'Pending Donor';
        case 'PENDING_BANK_SUBMISSIONS':
            return 'Active Cards';
        case 'COMPLETE':
            return 'Inactive Cards';
        case 'ALL':
            return 'All';
        case 'SEARCH':
            return 'Search';
        default:
            return tab;
    }
}

export const AR_TAB_QUERY_KEY = 'arTab';

export function arTabQueryValueForFilter(tab: AdvancedRollupFilterValue): string {
    switch (tab) {
        case 'TODO_NKR':
            return 'todo';
        case 'DECLINED':
            return 'declined';
        case 'ON_HOLD':
            return 'on-hold';
        case 'PENDING_DONOR':
            return 'pending-donor';
        case 'PENDING_BANK_SUBMISSIONS':
            return 'pending-bank-submissions';
        case 'COMPLETE':
            return 'complete';
        case 'ALL':
            return 'all';
        case 'SEARCH':
            return 'search';
        default:
            return 'all';
    }
}

export function tryParseArTabQueryValue(raw: string | string[] | null | undefined): AdvancedRollupFilterValue | null {
    const v = Array.isArray(raw) ? raw[0] : raw;
    if (typeof v !== 'string' || v.trim() === '') {
        return null;
    }
    const s = v.trim().toLowerCase();
    const map: Record<string, AdvancedRollupFilterValue> = {
        'invited-pending': 'PENDING_DONOR',
        'invited_pending': 'PENDING_DONOR',
        'todo': 'TODO_NKR',
        'approved': 'PENDING_BANK_SUBMISSIONS',
        'pending-bank-submissions': 'PENDING_BANK_SUBMISSIONS',
        'pending_bank_submissions': 'PENDING_BANK_SUBMISSIONS',
        'declined': 'DECLINED',
        'on-hold': 'ON_HOLD',
        'on_hold': 'ON_HOLD',
        'pending-donor': 'PENDING_DONOR',
        'pending_donor': 'PENDING_DONOR',
        'funded': 'COMPLETE',
        'complete': 'COMPLETE',
        'closed': 'COMPLETE',
        'inactive-card': 'COMPLETE',
        'inactive_card': 'COMPLETE',
        'all': 'ALL',
        'search': 'SEARCH',
    };
    return map[s] ?? null;
}
